import { useEffect, useState } from 'react'
import { getDownloadURL, ref } from 'firebase/storage'
import { storage } from '@/core/firebase'
import { MediaPicker } from '@/components/media/MediaPicker'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { MediaLightbox } from '@/components/session/MediaLightbox'
import { inferMediaType } from '@/lib/media'
import type { Media } from '@/types'
import { getVideoThumbnail, isEmbeddableVideo } from '@/lib/media'
import type { UploadingMedia } from '@/shared/hooks/useMediaUpload'

interface ExerciseMediaManagerProps {
  media: Media[]
  uploads: UploadingMedia[]
  isUploading: boolean
  onAddFiles: (files: FileList | null) => void
  onRemove: (mediaId: string) => Promise<void>
  onAddExternal: (url: string) => void
  onRetry?: (id: string) => void
  onRemoveUpload?: (id: string) => void
}

export function ExerciseMediaManager({
  media,
  uploads,
  isUploading,
  onAddFiles,
  onRemove,
  onAddExternal,
  onRetry,
  onRemoveUpload,
}: ExerciseMediaManagerProps) {
  const [resolvedMedia, setResolvedMedia] = useState<Media[]>(media)
  const [linkWarning, setLinkWarning] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<{ mediaId: string; name: string } | null>(null)
  const [lightboxMedia, setLightboxMedia] = useState<Media | null>(null)

  useEffect(() => {
    let cancelled = false
    const resolve = async () => {
      const results = await Promise.all(
        media.map(async (item) => {
          if (item.downloadUrl) return item
          try {
            const url = await getDownloadURL(ref(storage, item.storagePath))
            return { ...item, downloadUrl: url }
          } catch {
            return item
          }
        })
      )
      if (!cancelled) setResolvedMedia(results)
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [media])

  const handleRemoveClick = (mediaId: string, name: string) => {
    setConfirmRemove({ mediaId, name })
  }

  const handleConfirmRemove = async () => {
    if (confirmRemove) {
      await onRemove(confirmRemove.mediaId)
      setConfirmRemove(null)
    }
  }

  // Render preview - uses thumbnail for images/videos (faster list loading, clicks open lightbox)
  const renderPreview = (item: {
    url?: string
    thumbnailUrl?: string
    type: 'image' | 'video' | 'document'
    label: string
  }) => {
    if (item.type === 'video') {
      const thumbnail = getVideoThumbnail(item.url)
      const isEmbed = isEmbeddableVideo(item.url)
      return (
        <div className="relative w-full h-28 rounded-md overflow-hidden">
          {thumbnail ? (
            <img
              className="w-full h-full object-cover"
              src={thumbnail}
              alt={item.label}
              referrerPolicy="no-referrer"
            />
          ) : isEmbed ? (
            <div className="w-full h-full bg-slate-800 flex items-center justify-center">
              <span className="text-xs text-slate-400">Video</span>
            </div>
          ) : (
            <video
              className="w-full h-full object-cover"
              src={item.url}
              muted
              playsInline
              controls={false}
              preload="metadata"
            />
          )}
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <svg className="w-4 h-4 text-slate-800 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      )
    }
    if (item.type === 'document') {
      return (
        <div className="w-full max-w-full h-28 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center text-xs text-gray-700 dark:text-slate-300">
          <span className="px-3 py-1 rounded bg-primary-500/20 text-primary-700 dark:text-primary-100">PDF / doc</span>
        </div>
      )
    }
    // Use thumbnail for images if available (much faster loading in list views)
    const displayUrl = item.thumbnailUrl || item.url
    return (
      <img
        className="w-full max-w-full h-28 object-cover rounded-md"
        src={displayUrl}
        alt={item.label}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    )
  }

  const activeUploads = uploads.filter((u) => u.status === 'uploading' || u.status === 'failed')
  const uploadingCount = uploads.filter((u) => u.status === 'uploading').length
  const failedCount = uploads.filter((u) => u.status === 'failed').length

  return (
    <div className="space-y-3">
      <MediaPicker
        description="Add photos, videos, or links to help demonstrate this exercise"
        onFiles={(files) => {
          onAddFiles(files)
          setLinkWarning(null)
        }}
        onLink={(url) => {
          onAddExternal(url)
          const maybeType = inferMediaType(url)
          setLinkWarning(
            maybeType ? null : 'Unknown types fall back to images; adjust after upload if needed.'
          )
        }}
        allowMultiple
        accept="image/*,video/*,application/pdf"
        cameraAccept="image/*,video/*"
        capture="environment"
        busyText={isUploading ? 'Uploading...' : undefined}
        variant="inline"
      />

      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-600 dark:text-slate-400">
          {media.length} saved
        </span>
        {uploadingCount > 0 && (
          <span className="text-cyan-600 dark:text-cyan-400">
            · {uploadingCount} uploading
          </span>
        )}
        {failedCount > 0 && (
          <span className="text-red-600 dark:text-red-400">
            · {failedCount} failed
          </span>
        )}
      </div>

      {linkWarning && <p className="text-xs text-amber-600 dark:text-amber-300">{linkWarning}</p>}

      <div className="grid sm:grid-cols-3 gap-2 overflow-hidden">
        {/* Active uploads (uploading or failed) */}
        {activeUploads.map((upload) => (
          <div
            key={upload.id}
            className={`rounded-lg border p-2 space-y-2 min-w-0 overflow-hidden ${
              upload.status === 'failed'
                ? 'border-red-400/40 bg-red-500/10'
                : 'border-cyan-400/40 bg-cyan-500/10'
            }`}
          >
            {/* Preview */}
            <div className="relative">
              {renderPreview({ url: upload.previewUrl, type: upload.type, label: upload.file.name })}

              {/* Progress overlay */}
              {upload.status === 'uploading' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin mx-auto" />
                    <p className="text-xs text-white mt-2 font-medium">{upload.progress}%</p>
                  </div>
                </div>
              )}

              {/* Failed overlay */}
              {upload.status === 'failed' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
                  <div className="text-center px-2">
                    <span className="text-red-400 text-2xl">!</span>
                    <p className="text-xs text-red-300 mt-1">Upload failed</p>
                  </div>
                </div>
              )}
            </div>

            {/* Info and actions */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] truncate flex-1 text-gray-700 dark:text-slate-300">
                {upload.file.name}
              </p>
              <div className="flex gap-1">
                {upload.status === 'failed' && onRetry && (
                  <button
                    onClick={() => onRetry(upload.id)}
                    className="text-[11px] text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300"
                  >
                    Retry
                  </button>
                )}
                {onRemoveUpload && (
                  <button
                    onClick={() => onRemoveUpload(upload.id)}
                    className="text-[11px] text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Saved media */}
        {resolvedMedia.map((item) => (
          <div
            key={item.mediaId}
            className="rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-2 space-y-2 min-w-0 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setLightboxMedia(item)}
              className="w-full cursor-pointer hover:opacity-80 transition-opacity"
            >
              {item.downloadUrl
                ? renderPreview({
                    url: item.downloadUrl,
                    thumbnailUrl: item.thumbnailUrl,
                    type: item.type,
                    label: item.caption || '',
                  })
                : item.externalUrl
                  ? renderPreview({ url: item.externalUrl, type: item.type, label: item.caption || '' })
                  : (
                    <div className="w-full h-28 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center text-xs text-gray-500 dark:text-slate-400">
                      No preview
                    </div>
                  )}
            </button>
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-gray-700 dark:text-slate-300 truncate flex-1">
                {item.caption || item.storagePath?.split('/').pop() || item.externalUrl || 'Media'}
              </p>
              <button
                onClick={() => handleRemoveClick(
                  item.mediaId,
                  item.caption || item.storagePath?.split('/').pop() || 'this media'
                )}
                className="text-[11px] text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-100"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Confirmation dialog */}
      <ConfirmDialog
        isOpen={!!confirmRemove}
        title="Remove media?"
        message={`This will permanently delete "${confirmRemove?.name}" from storage. This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        variant="danger"
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmRemove(null)}
      />

      {/* Media lightbox */}
      <MediaLightbox media={lightboxMedia} onClose={() => setLightboxMedia(null)} />
    </div>
  )
}
